import * as R from 'remeda';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import { createTheme, ThemeOptions } from '../material';
import { commonThemeOptions } from './common';
import { MuiAlert } from './component-overrides/alert';
import { MuiAutocomplete } from './component-overrides/autocomplete';
import { MuiAvatar, MuiAvatarGroup } from './component-overrides/avatar';
import { MuiButton } from './component-overrides/button';
import { MuiButtonGroup } from './component-overrides/buttonGroup';
import { MuiCheckbox } from './component-overrides/checkbox';
import { MuiChip } from './component-overrides/chip';
import { MuiCssBaseline } from './component-overrides/cssBaseline';
import { MuiDataGrid } from './component-overrides/dataGrid';
import { MuiDialog } from './component-overrides/dialog';
import { MuiDrawer } from './component-overrides/drawer';
import { MuiIconButton } from './component-overrides/iconButton';
import { MuiLink } from './component-overrides/link';
import { MuiMenu } from './component-overrides/menu';
import { MuiPaper } from './component-overrides/paper';
import { MuiPopover } from './component-overrides/popover';
import { MuiRadio } from './component-overrides/radio';
import {
  MuiStepConnector,
  MuiStepIcon,
  MuiStepLabel,
  MuiStepper,
} from './component-overrides/stepper';
import { MuiSvgIcon } from './component-overrides/svgIcon';
import { MuiSwitch } from './component-overrides/switch';
import { MuiTableCell } from './component-overrides/tableCell';
import { MuiTableRow } from './component-overrides/tableRow';
import { MuiSelect } from './component-overrides/textField';
import {
  MuiToggleButton,
  MuiToggleButtonGroup,
} from './component-overrides/toggleButton';
import { MuiTooltip } from './component-overrides/tooltip';
import { coupaThemeOptions } from './coupa';
import { darkThemeOptions } from './dark';
import { lightThemeOptions } from './light';

const fontWeightRegular = 400;
export const HTML_FONT_SIZE = 10;

export type ThemeMode = 'light' | 'dark' | 'coupa';

const themeOptionsByMode: Record<ThemeMode, ThemeOptions> = {
  light: lightThemeOptions,
  dark: darkThemeOptions,
  coupa: coupaThemeOptions,
};

const pxToRem = (size: number) => {
  return `${size / HTML_FONT_SIZE}rem`;
};

const resolveFont = (mode: ThemeMode, customOptions?: ThemeOptions) =>
  (customOptions?.typography && 'fontFamily' in customOptions.typography) ||
  (themeOptionsByMode[mode].typography &&
    'fontFamily' in themeOptionsByMode[mode].typography)
    ? {}
    : { typography: { fontFamily: 'Inter, Arial, sans-serif' } };

const resolveFontWeight = () => ({
  typography: {
    fontWeightMedium: 600,
  },
});

export const resolveTheme = (mode: ThemeMode, customOptions?: ThemeOptions) => {
  const resolvedOptions = R.pipe(
    commonThemeOptions as Record<string, unknown>,
    R.mergeDeep(themeOptionsByMode[mode] as Record<string, unknown>),
    R.mergeDeep(customOptions as Record<string, unknown>),
    R.mergeDeep(resolveFont(mode, customOptions)),
    R.mergeDeep(resolveFontWeight())
  );

  const { typography } = resolvedOptions;
  const { fontWeightMedium } = typography;

  const baseTypography = {
    h3: {
      fontWeight: fontWeightMedium,
      fontSize: pxToRem(32),
      lineHeight: 1.25,
      letterSpacing: 0,
    },
    h4: {
      fontWeight: fontWeightMedium,
      fontSize: pxToRem(20),
      lineHeight: 1.4,
      letterSpacing: 0,
    },
    h5: {
      fontWeight: fontWeightMedium,
      fontSize: pxToRem(20),
      lineHeight: 1.4,
      letterSpacing: 0,
    },
    h6: {
      fontWeight: fontWeightMedium,
      fontSize: pxToRem(16),
      lineHeight: 1.5,
      letterSpacing: 0,
    },
    subtitle2: {
      fontWeight: fontWeightMedium,
      fontSize: pxToRem(14),
      lineHeight: 1.43,
      letterSpacing: 0.2,
    },
    body1: {
      fontWeight: fontWeightRegular,
      fontSize: pxToRem(16),
      lineHeight: 1.5,
      letterSpacing: 0,
    },
    body2: {
      fontWeight: fontWeightRegular,
      fontSize: pxToRem(14),
      lineHeight: 1.43,
      letterSpacing: 0,
    },
    button: {
      fontWeight: fontWeightMedium,
      fontSize: pxToRem(14),
      lineHeight: 1.43,
      letterSpacing: 0,
      textTransform: 'none',
    },
    caption: {
      fontWeight: fontWeightRegular,
      fontSize: pxToRem(12),
      lineHeight: 1.34,
      letterSpacing: 0,
    },
  };

  const resolvedTypography = R.mergeDeep(
    baseTypography as Record<string, unknown>,
    resolvedOptions.typography as Record<string, unknown>
  );

  const sharedComponents = {
    MuiDataGrid,
    MuiCssBaseline,
    MuiCheckbox,
    MuiChip,
    MuiSelect,
    MuiAutocomplete,
    MuiButton,
    MuiButtonGroup,
    MuiDrawer,
    MuiIconButton,
    MuiLink,
    MuiPaper,
    MuiStepper,
    MuiStepLabel,
    MuiStepIcon,
    MuiStepConnector,
    MuiTableCell,
    MuiTableRow,
    MuiTooltip,
    MuiMenu,
    MuiPopover,
    MuiAlert,
    MuiDialog,
    MuiToggleButton,
    MuiToggleButtonGroup,
    MuiSwitch,
    MuiRadio,
    MuiAvatar,
    MuiAvatarGroup,
    MuiSvgIcon,
  };

  const resolvedComponents = R.mergeDeep(
    sharedComponents as Record<string, unknown>,
    (themeOptionsByMode[mode].components ?? {}) as Record<string, unknown>
  );

  const theme = createTheme({
    ...resolvedOptions,
    components: resolvedComponents,
    typography: resolvedTypography,
  });

  return theme;
};
